/**
 * Setting up a way to prove it is you — `docs/identity-and-sign-in.md` §4.3,
 * mockup board 3.
 *
 * **One dialog, four methods, and a progress bar to two.** It opens from My
 * profile, from the invite-accept flow when somebody has nothing set up, and
 * at sign-in when a role requires a method and there is none. The four cards
 * come from `shared/src/mfa.ts`, so the order, the words and which one is
 * recommended are the same here as in *Try another way* and on the server.
 *
 * Two rules the mockup makes and this keeps:
 *
 * - **Every method ends in the same recovery-codes panel, the first time.**
 *   Not every time: somebody adding a third passkey being handed ten new codes
 *   would silently retire the ten they printed. The server decides (it knows
 *   how many methods existed before) and this draws what it sends back.
 * - **Later is absent when a role requires two-step.** `policy.canSkip` comes
 *   from the same role read the sign-in path makes, so the dialog cannot offer
 *   an escape the server would refuse.
 */
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react'
import {
  MFA_ORDER, MFA_KIND_INFO, MFA_WANTED,
  type MfaKind, type MfaMethod, type MfaPolicy,
} from '@rowted/shared'
import { useAccountApi } from '../account/account-api.js'
import { createPasskey } from '../lib/passkey.js'
import { ApiError } from '@rowted/shared'
import { Modal } from './overlays.js'
import { Button, Checkbox, Tag } from './primitives.js'
import { CodeInput } from './code-input.js'
import { Field } from './fields.js'
import {
  IconCheck, IconEmail, IconFingerprint, IconPinCode, IconSecurity, IconSms, IconSpinner,
} from '../frame/icons.js'
import { cn } from '../lib/cn.js'

interface MethodsAnswer {
  methods: MfaMethod[]
  policy: MfaPolicy
  destinations: { email: string; phone: string | null }
  wantsAnother: boolean
  /** What this door takes; absent means all four. See `sign-in-methods.tsx`. */
  kinds?: readonly MfaKind[]
  /** How many single-use codes are still good. Both sides send it. */
  recoveryCodesLeft?: number
}

/** Which panel the dialog is showing. */
type Step =
  | { at: 'choose' }
  | { at: 'totp'; methodId: string; uri: string; qr: string; manualKey: string;
      account: string }
  | { at: 'sms'; sentTo: string }
  | { at: 'passkey' }
  | { at: 'codes'; codes: string[] }

export function TwoStepSetup({ onClose, onChanged, begin }: {
  onClose: () => void
  /** So the profile behind the dialog can redraw without a full reload. */
  onChanged?: (answer: MethodsAnswer) => void
  /**
   * Open straight into one method rather than the chooser.
   *
   * This is what **Reset** on the Sign-in methods card means. It passed a kind
   * to `onAdd` from the day it was written and the dialog ignored it, so Reset
   * opened the chooser — where the authenticator is already **On** and so has
   * no button at all. The control was a dead end on both sides, and the only
   * way out was to turn the method off and set it up again.
   */
  begin?: MfaKind
}) {
  const [answer, setAnswer] = useState<MethodsAnswer | null>(null)
  const [step, setStep] = useState<Step>({ at: 'choose' })
  const [code, setCode] = useState('')
  const [label, setLabel] = useState('')
  const [syncable, setSyncable] = useState(true)
  const [busy, setBusy] = useState<MfaKind | 'confirm' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const { request, guard } = useAccountApi()

  const load = useCallback(async () => {
    const next = await request<MethodsAnswer>('/me/methods')
    setAnswer(next)
    onChanged?.(next)
    return next
  }, [onChanged, request])

  useEffect(() => { void load().catch(() => setError('Could not read your settings.')) }, [load])

  /*
   * Every route under `/me/methods` that changes a method is a step-up, so
   * the guard sits here rather than at each call site — one place that can ask
   * for the password and replay, instead of seven that each print the refusal.
   * Reading the list is not guarded, which is why `load()` runs outside it.
   */
  const run = async (kind: MfaKind | 'confirm', work: () => Promise<void>) => {
    setError(null)
    setBusy(kind)
    try {
      await guard(work)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Something went wrong. Try again.')
    } finally {
      setBusy(null)
    }
  }

  /**
   * What the server sent back after a method went on.
   *
   * `recoveryCodes` is non-null only the first time, so this is also what
   * decides whether the codes panel appears at all.
   */
  const settled = (result: MethodsAnswer & { recoveryCodes?: string[] | null }) => {
    setAnswer(result)
    onChanged?.(result)
    setCode('')
    setStep(result.recoveryCodes?.length
      ? { at: 'codes', codes: result.recoveryCodes }
      : { at: 'choose' })
  }

  /*
   * `begin`, once. A ref rather than a dependency on `step`, because `start`
   * changes `step` and a dependency on it would run this again the moment it
   * succeeded.
   */
  const began = useRef(false)

  const start = (kind: MfaKind) => run(kind, async () => {
    if (kind === 'totp') {
      const setup = await request<{
        methodId: string; uri: string; qr: string; manualKey: string; account: string
      }>('/me/methods/totp/start', { method: 'POST', body: {} })
      setStep({ at: 'totp', ...setup })
      return
    }
    if (kind === 'passkey') {
      setLabel(suggestLabel())
      setStep({ at: 'passkey' })
      return
    }
    const answered = await request<
      MethodsAnswer & { confirmed: boolean; sentTo?: string; recoveryCodes?: string[] | null }
    >('/me/methods/code/start', { method: 'POST', body: { kind } })
    if (answered.confirmed) settled(answered)
    else setStep({ at: 'sms', sentTo: answered.sentTo ?? 'your mobile' })
  })

  /**
   * Turning one off, from the one screen that lists them.
   *
   * The profile's six-row card used to own this; it is two rows now (Aaron's
   * review) and this dialog is where every method is changed, so removing one
   * had to come with it. The server refuses the last one when a role requires
   * two-step, and the card disables the button for the same case rather than
   * letting somebody find out by pressing it.
   */
  const remove = (method: MfaMethod) => run(method.kind, async () => {
    settled(await request(`/me/methods/${method.id}/disable`, { method: 'POST', body: {} }))
  })

  /** A fresh ten. The old set is superseded, never deleted (rule 2). */
  const regenerate = () => run('confirm', async () => {
    const answered = await request<MethodsAnswer & { recoveryCodes: string[] }>(
      '/me/methods/recovery-codes', { method: 'POST', body: {} })
    settled(answered)
  })

  useEffect(() => {
    if (!begin || began.current) return
    began.current = true
    void start(begin)
    /* `start` is rebuilt every render, so it is deliberately not a dependency;
       the ref is what makes this run once. */
  }, [begin, start])

  /*
   * Board 3 numbers the method panels, so the person can see the dialog is two
   * screens deep and not open-ended. The chooser is step one and says so by
   * carrying no marker of its own — see `headerRight` below.
   */
  const held = new Set((answer?.methods ?? []).map((one) => one.kind))
  const count = answer?.methods.length ?? 0
  const recoveryCodesLeft = answer?.recoveryCodesLeft ?? null
  const canSkip = answer?.policy.canSkip ?? true

  return (
    <Modal
      open
      onClose={onClose}
      width={520}
      title={titleFor(step)}
      subtitle={subtitleFor(step, answer)}
      headerRight={step.at === 'choose' ? undefined : (
        <span className="text-[11.5px] text-text-faint">Step 2 of 2</span>
      )}
      footerNote={step.at === 'passkey'
        ? 'No passkey support here? Use an authenticator app instead.'
        : undefined}
      footer={step.at === 'choose' ? (
        <>
          {/*
            * Absent, not disabled, when a role requires a method. A disabled
            * Later is an offer somebody keeps pressing; an absent one says the
            * decision was made above their head, which it was.
            */}
          {canSkip && <Button onClick={onClose}>Later</Button>}
          {!canSkip && (
            <span className="text-cell text-text-muted">
              Owners and admins can’t skip this
            </span>
          )}
          <Button variant="primary" disabled={count === 0} onClick={onClose}>
            Done for now
          </Button>
        </>
      ) : step.at === 'codes' ? (
        <Button variant="primary" onClick={() => setStep({ at: 'choose' })}>
          I’ve saved these
        </Button>
      ) : (
        <>
          <Button onClick={() => { setStep({ at: 'choose' }); setCode(''); setError(null) }}>
            Back
          </Button>
          {step.at === 'totp' && (
            <Button
              variant="primary"
              disabled={code.length < 6 || busy !== null}
              onClick={() => void run('confirm', async () => {
                settled(await request('/me/methods/totp/confirm', {
                  method: 'POST', body: { methodId: step.methodId, code },
                }))
              })}
            >
              {busy === 'confirm' ? <IconSpinner size={15} /> : null}
              Verify and turn on
            </Button>
          )}
          {step.at === 'sms' && (
            <Button
              variant="primary"
              disabled={code.length < 6 || busy !== null}
              onClick={() => void run('confirm', async () => {
                settled(await request('/me/methods/code/confirm', {
                  method: 'POST', body: { code },
                }))
              })}
            >
              {busy === 'confirm' ? <IconSpinner size={15} /> : null}
              Verify and turn on
            </Button>
          )}
          {step.at === 'passkey' && (
            <Button
              variant="primary"
              disabled={!label.trim() || busy !== null}
              onClick={() => void run('confirm', async () => {
                settled(await createPasskey(request, label, syncable))
              })}
            >
              {busy === 'confirm' ? <IconSpinner size={15} /> : null}
              Create passkey
            </Button>
          )}
        </>
      )}
    >
      <div className="flex flex-col gap-4 p-4">
        {error && (
          <p role="alert" className="m-0 rounded-field bg-danger-bg px-3 py-2 text-cell
                                     text-danger">
            {error}
          </p>
        )}

        {step.at === 'choose' && (
          <>
            <div className="flex flex-col gap-2">
              {/* Only the kinds this door takes — Rowted Admin has no code
                  store, so an email card there would set up a way in that the
                  sign-in refuses. `answer.kinds` is the server saying so. */}
              {MFA_ORDER
                .filter((kind) => !answer?.kinds || answer.kinds.includes(kind))
                .map((kind) => (
                <MethodCard
                  key={kind}
                  kind={kind}
                  on={held.has(kind)}
                  busy={busy === kind}
                  destination={destinationFor(kind, answer)}
                  /* The last one standing stays when a role requires two-step. */
                  canRemove={!(answer?.policy.required ?? false) || count > 1}
                  onStart={() => void start(kind)}
                  onRemove={() => {
                    const method = answer?.methods.find((one) => one.kind === kind)
                    if (method) void remove(method)
                  }}
                />
                ))}
            </div>
            {count > 0 && recoveryCodesLeft !== null && (
              <RecoveryRow
                left={recoveryCodesLeft}
                busy={busy === 'confirm'}
                onRegenerate={() => void regenerate()}
              />
            )}
            <Progress count={count} />
          </>
        )}

        {step.at === 'totp' && (
          <TotpStep setup={step} code={code} onCode={setCode} />
        )}

        {step.at === 'sms' && (
          <div className="flex flex-col gap-3">
            <p className="m-0 text-body leading-[1.45] text-text-muted">
              We texted a code to <b className="text-text">{step.sentTo}</b>. It works for
              10 minutes.
            </p>
            <CodeInput value={code} onChange={setCode} autoFocus label="Code" />
          </div>
        )}

        {step.at === 'passkey' && (
          <PasskeyStep
            label={label} onLabel={setLabel}
            syncable={syncable} onSyncable={setSyncable}
          />
        )}

        {step.at === 'codes' && <RecoveryCodesPanel codes={step.codes} />}
      </div>
    </Modal>
  )
}

/* ---------------------------------------------------------------- panels -- */

/**
 * One tile per kind, so the four cards are told apart before they are read.
 * Board 3 draws them at 36px in a tinted square, green once the method is on.
 */
const KIND_ICON: Record<MfaKind, (props: { size?: number }) => ReactElement> = {
  passkey: IconFingerprint,
  totp: IconSecurity,
  email: IconEmail,
  sms: IconSms,
}

/**
 * One card per kind.
 *
 * **An enrolled card offers the two things you came for.** It used to carry a
 * tinted *Set up ✓* chip — a label dressed as a button, which told somebody
 * the method was on and gave them nothing to do about it. Turning one off
 * lived on the profile's six-row card, and when that collapsed to two rows
 * (Aaron's review) this became the only place either act could live. So:
 * **Remove** always, and a second verb for the two kinds that can genuinely be
 * re-enrolled — another passkey for another device, a fresh QR when a phone is
 * replaced. An email or a text has nothing to edit: the address is the one you
 * sign in with and the number is on your profile.
 */
function MethodCard({ kind, on, busy, destination, canRemove, onStart, onRemove }: {
  kind: MfaKind
  on: boolean
  busy: boolean
  destination: string | null
  /** False for the last one standing when a role requires two-step. */
  canRemove: boolean
  onStart: () => void
  onRemove: () => void
}) {
  const info = MFA_KIND_INFO[kind]
  const Icon = KIND_ICON[kind]
  /* A passkey is one more per device; an authenticator is one, re-scanned. */
  const again = kind === 'passkey' ? 'Add another' : kind === 'totp' ? 'Replace' : null

  return (
    <div className={cn('flex items-start gap-3 rounded-popup border px-3.5 py-3',
      on ? 'border-accent-line bg-accent-soft' : 'border-line bg-surface')}
    >
      <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-field',
        on ? 'bg-white text-accent-dark' : 'bg-field text-text-muted')}
      >
        <Icon size={17} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex flex-wrap items-center gap-2">
          <b className="text-card text-text">{info.name}</b>
          {on
            ? <Tag tone="ok"><IconCheck size={11} />Enrolled</Tag>
            : info.recommended && <Tag tone="ok">Recommended</Tag>}
        </span>
        <span className="text-cell leading-[1.45] text-text-muted">
          {destination ?? info.blurb}
        </span>
      </span>
      {on ? (
        <span className="flex shrink-0 items-center gap-1.5">
          {again && (
            <Button className="h-[30px]" disabled={busy} onClick={onStart}>{again}</Button>
          )}
          <Button
            className="h-[30px]"
            /*
             * The server refuses the last one anyway when a role requires
             * two-step; disabling it is so nobody has to find that out by
             * pressing it.
             */
            disabled={busy || !canRemove}
            title={canRemove ? undefined : 'Your role requires a second step.'}
            onClick={onRemove}
          >
            Remove
          </Button>
        </span>
      ) : (
        <Button className="h-[30px] shrink-0" disabled={busy} onClick={onStart}>
          {busy ? <IconSpinner size={14} /> : null}
          Set up
        </Button>
      )}
    </div>
  )
}

/**
 * Recovery codes, at the foot of the list.
 *
 * Not a card like the four above it, because it is not a way in you choose —
 * it is what is left when every one of them has failed, and drawing it as a
 * fifth method would offer it as an alternative to a passkey. A rule and a
 * quiet row says what it is: a number, and one button.
 *
 * **There is no View.** `recovery_codes` keeps a hash and nothing else, so
 * showing a code again would mean storing it in readable form. Regenerate is
 * what is left, and the codes exist in readable form exactly once — on the
 * panel that hands them over.
 */
function RecoveryRow({ left, busy, onRegenerate }: {
  left: number
  busy: boolean
  onRegenerate: () => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-line-soft pt-3">
      <span className="flex w-5 shrink-0 justify-center text-text-muted">
        <IconPinCode size={17} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-px">
        <b className="text-card text-text">Recovery codes</b>
        <span className="text-cell text-text-muted">
          each works once · keep them somewhere safe
        </span>
      </span>
      <span className={cn('shrink-0 text-cell font-semibold',
        left > 2 ? 'text-text-muted' : 'text-warn')}
      >
        {left} of 10 left
      </span>
      <Button className="h-[30px] shrink-0" disabled={busy} onClick={onRegenerate}>
        {busy ? <IconSpinner size={14} /> : null}
        Regenerate
      </Button>
    </div>
  )
}

/** Two segments, because two is where the nag stops (§4.3). */
function Progress({ count }: { count: number }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="flex gap-1">
        {Array.from({ length: MFA_WANTED }, (_, index) => (
          <span
            key={index}
            className={cn('h-1 flex-1 rounded-full',
              index < count ? 'bg-accent' : 'bg-line')}
          />
        ))}
      </span>
      <span className="text-[11.5px] text-text-muted">
        <b className="text-text">{Math.min(count, MFA_WANTED)} of {MFA_WANTED}</b>
        {' · add a second way so a lost phone is not a lost account'}
      </span>
    </div>
  )
}

function TotpStep({ setup, code, onCode }: {
  setup: { qr: string; manualKey: string; account: string }
  code: string
  onCode: (next: string) => void
}) {
  return (
    <div className="flex flex-col gap-3">
      {/* QR beside the key, not above it: board 3 puts them side by side so
          the person who cannot scan does not have to scroll to find out. */}
      <div className="flex items-start gap-3.5">
        <span className="flex shrink-0 rounded-popup border border-line bg-white p-2">
          <img src={setup.qr} alt="" className="size-[104px]" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-cell text-text-muted">
            Can’t scan? Enter this key by hand
          </span>
          {/* Grouped in fours, which is how every authenticator prints it. */}
          <code className="rounded-field border border-line bg-field px-2.5 py-2
                           text-body tracking-[0.08em] text-text">
            {setup.manualKey.replace(/(.{4})/g, '$1 ').trim()}
          </code>
          <span className="text-[11.5px] leading-[1.4] text-text-faint">
            {`Account: ${setup.account} · time-based · 30 s`}
          </span>
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <span className="text-cell font-semibold text-text">Enter the 6-digit code</span>
        <CodeInput value={code} onChange={onCode} autoFocus label="The 6-digit code" />
      </div>
      {/*
        * Said before it happens. The codes panel is the only time those ten
        * codes exist in readable form, and somebody who does not know it is
        * coming closes the dialog on them.
        */}
      <p className="m-0 flex items-start gap-2 rounded-popup border border-accent-line
                    bg-accent-soft px-3 py-2.5 text-cell leading-[1.45] text-text-body">
        <IconPinCode size={14} />
        <span>
          <b className="text-text">Then your recovery codes</b>
          {' Ten one-time codes shown once — download or copy them before you close this.'}
        </span>
      </p>
    </div>
  )
}

function PasskeyStep({ label, onLabel, syncable, onSyncable }: {
  label: string
  onLabel: (next: string) => void
  syncable: boolean
  onSyncable: (next: boolean) => void
}) {
  return (
    <div className="flex flex-col gap-3">
      {/*
        * Board 3 puts this sentence under the dialog title. It is in the body
        * because `Modal`'s subtitle is a clause *beside* the title — a
        * three-line one there would stretch every dialog's header, and bending
        * the component for one screen is the drift §9 asks about.
        */}
      <p className="m-0 text-body leading-[1.45] text-text-muted">
        It lives on this device (or in your password manager) and signs you in with a touch.
      </p>
      <div className="flex flex-col items-center gap-2 rounded-popup border border-line
                      bg-surface-alt px-4 py-5 text-center">
        <span className="flex size-11 items-center justify-center rounded-full bg-accent-soft
                         text-accent-dark">
          <IconFingerprint size={20} />
        </span>
        <b className="text-card text-text">Your device is asking</b>
        <span className="max-w-[340px] text-cell leading-[1.45] text-text-muted">
          Use your face, fingerprint or device PIN. Rowted never sees it — the device keeps
          the key and only ever hands back a signature.
        </span>
      </div>
      <Field
        label="Name this passkey"
        value={label}
        onChange={onLabel}
        required
        hint="So you can tell them apart on your profile."
      />
      {/*
        * Not decoration: ticked leaves `authenticatorAttachment` unset, so a
        * password manager may hold the key and it works on the person's other
        * devices; unticked asks for `platform`, which keeps it on this one.
        * A checkbox that only changed a word would be worse than none.
        */}
      <Checkbox
        align="start"
        checked={syncable}
        onChange={(event) => onSyncable(event.currentTarget.checked)}
        label={(
          <span className="flex flex-col gap-0.5">
            <b className="text-cell text-text">Also sync it through my password manager</b>
            <span className="text-[11.5px] text-text-muted">
              1Password, iCloud Keychain, Google Password Manager
            </span>
          </span>
        )}
      />
    </div>
  )
}

/**
 * Ten codes, shown once.
 *
 * Download and Copy are both here because the panel is the only time these
 * exist in readable form — `issueRecoveryCodes` keeps only a hash, so a person
 * who closes this without saving them has lost them, and the wording says so.
 */
export function RecoveryCodesPanel({ codes }: { codes: string[] }) {
  const [copied, setCopied] = useState(false)
  const text = codes.join('\n')

  return (
    <div className="flex flex-col gap-3">
      <p className="m-0 text-body leading-[1.45] text-text-muted">
        Ten one-time codes, shown once. Save them somewhere safe — they are the way back
        in if you lose your phone.
      </p>
      <div className="grid grid-cols-2 gap-1.5 rounded-popup border border-line bg-field p-3">
        {codes.map((one) => (
          <code key={one} className="text-body tracking-[0.06em] text-text">{one}</code>
        ))}
      </div>
      <span className="flex gap-2">
        <Button
          onClick={() => {
            const blob = new Blob([text], { type: 'text/plain' })
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = 'rowted-recovery-codes.txt'
            link.click()
            URL.revokeObjectURL(url)
          }}
        >
          Download
        </Button>
        <Button
          onClick={() => {
            void navigator.clipboard?.writeText(text).then(() => setCopied(true))
          }}
        >
          {copied ? <IconCheck size={14} /> : null}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </span>
    </div>
  )
}

/* ----------------------------------------------------------------- words -- */

function titleFor(step: Step): string {
  switch (step.at) {
    case 'totp': return 'Add an authenticator app'
    case 'sms': return 'Confirm your mobile'
    case 'passkey': return 'Create a passkey'
    case 'codes': return 'Your recovery codes'
    default: return 'Set up two-step sign-in'
  }
}

function subtitleFor(step: Step, answer: MethodsAnswer | null): string {
  if (step.at !== 'choose') return ''
  return answer && answer.methods.length > 0
    ? 'Add another way in, so a lost phone is not a lost account.'
    : 'Pick one now, add a second after. Rowted asks for it when you sign in and before '
      + 'sensitive actions.'
}

/**
 * The blurb with the person's own address in it.
 *
 * It replaces the address, not the sentence: board 3 reads "A code sent to
 * afogleman@rowted.com. Good as a backup, slow every day" — the second half is
 * what tells somebody whether to pick it, and dropping it left two of the four
 * cards saying less than the other two.
 */
function destinationFor(kind: MfaKind, answer: MethodsAnswer | null): string | null {
  if (!answer) return null
  if (kind === 'email') {
    return `A code sent to ${answer.destinations.email}. Good as a backup, slow every day.`
  }
  if (kind === 'sms') {
    return answer.destinations.phone
      ? `A code texted to ${answer.destinations.phone}. Works anywhere, weakest of the four.`
      : 'Add a mobile number to your profile first.'
  }
  return null
}

/**
 * `koowali02 · Windows Hello` — a guess, and the field stays editable.
 *
 * The browser will not say which authenticator answered until after the
 * credential exists, and by then the person has moved on. A platform guess
 * from the user agent is right often enough to save typing and wrong in a way
 * anybody can see and correct.
 */
function suggestLabel(): string {
  const agent = navigator.userAgent
  if (/Windows/.test(agent)) return 'This PC · Windows Hello'
  if (/Mac OS X/.test(agent)) return 'This Mac · Touch ID'
  if (/iPhone|iPad/.test(agent)) return 'iPhone · Face ID'
  if (/Android/.test(agent)) return 'Android · fingerprint'
  return 'This device'
}
